/**
 * MPH-958: which warnings a leg of the itinerary shows above its stop list, and in which order.
 *
 * The outbound is the reference the return is compared against, so every airport change is said on
 * the return leg alone — Figma 5193-54430 / 55505 / 56043 warn there and leave the outbound silent
 * even though its code is highlighted too. The overnight message is per leg, since it is about that
 * leg's own arrival.
 */

/** Whether the leg's endpoints are airports or stations, which is all the copy distinguishes. */
export type EvoLegPlaceKind = 'airport' | 'station';

const PLACE_PREFIX: Record<EvoLegPlaceKind, string> = {
  airport: 'widget.trip_card.details.different_airport.',
  station: 'widget.trip_card.details.different_station.',
};

/**
 * The place-agnostic wording, kept as a second choice: the airport and station sets are published
 * in en, es, de and fr only, while this one is translated in all thirteen markets for the two
 * single-end cases. A market without the specific copy therefore reads "a different location"
 * rather than English.
 */
const LOCATION_PREFIX = 'widget.trip_card.details.different_location.';

const DIFFERENT_DEPARTURE = 'different_departure';
const DIFFERENT_ARRIVAL = 'different_arrival';
/** Both ends of the return flagged at once, the case Figma 5193-56043 draws. */
const DEPARTURE_AND_ARRIVAL = 'departure_and_arrival';

const NEXT_DAY_KEY = 'widget.trip_card.details.plus_days.next_day';
/** Two days or more; carries a `__days__` placeholder, unlike the one-day copy. */
const MORE_DAYS_KEY = 'widget.trip_card.details.plus_days.more_days';

/**
 * MPH-958: the combined message has no translation outside en, es, de and fr in any of the three
 * sets, and t() answers '' for a key it does not know — which would leave the banner without its
 * airport line. Remove the literal once the copy is translated everywhere.
 */
const DEPARTURE_AND_ARRIVAL_FALLBACK = 'Departs from and arrives at different locations';

export type EvoLegWarningsInput = {
  isInbound: boolean;
  /** Airports or stations, per the leg's own transport type. */
  placeKind: EvoLegPlaceKind;
  /** Leaves from a place the other leg does not use — the trip's *destination* differs. */
  departsFromDifferentLocation: boolean;
  /** Lands at a place the other leg does not use — the trip's *origin* differs. */
  arrivesAtDifferentLocation: boolean;
  /** Whole days between the leg's own departure and its arrival: above zero it lands on another date. */
  plusDays: number;
};

export function evoLegWarnings(
  { isInbound, placeKind, departsFromDifferentLocation, arrivesAtDifferentLocation, plusDays }: EvoLegWarningsInput,
  t: (key: string, interpolation?: Record<string, string>) => string
): string[] {
  const placeLabel = (name: string): string => t(`${PLACE_PREFIX[placeKind]}${name}`) || t(`${LOCATION_PREFIX}${name}`);

  const messages: string[] = [];

  if (isInbound) {
    if (departsFromDifferentLocation && arrivesAtDifferentLocation) {
      messages.push(placeLabel(DEPARTURE_AND_ARRIVAL) || DEPARTURE_AND_ARRIVAL_FALLBACK);
    } else if (departsFromDifferentLocation) {
      messages.push(placeLabel(DIFFERENT_DEPARTURE));
    } else if (arrivesAtDifferentLocation) {
      messages.push(placeLabel(DIFFERENT_ARRIVAL));
    }
  }

  // Figma only draws the one-day case, but a leg can gain more than one — the deck's
  // 'flight-arrives-two-days-later' card is there to show it, and saying "the next day" about a
  // two-day gain is simply wrong, so the counted copy takes over from two days up.
  if (plusDays === 1) {
    messages.push(t(NEXT_DAY_KEY));
  } else if (plusDays > 1) {
    messages.push(t(MORE_DAYS_KEY, { days: String(plusDays) }));
  }

  // A key missing from the dictionary resolves to '', which would draw an empty row.
  return messages.filter(Boolean);
}
